/**
 * Rulings Section - Card rulings from Scryfall
 *
 * Shared component for displaying card rulings across multiple contexts:
 * - Collection card details
 * - List card details
 * - Starred card details
 */
import clsx from 'clsx';
import { format } from 'date-fns';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { IoChevronDown, IoChevronUp, IoInformationCircle } from 'react-icons/io5';

import useCardRulings, { type CardRuling } from '@/hooks/useCardRulings';

import { RulingsContentSkeleton } from './RulingsSectionSkeleton';

export interface RulingsSectionProps {
  /** Scryfall card ID to fetch rulings for */
  cardId: string;
}

/** Number of rulings to show before collapsing */
const INITIAL_RULINGS_COUNT = 3;

interface RulingItemProps {
  ruling: CardRuling;
}

function RulingItem({ ruling }: RulingItemProps): ReactElement {
  const formattedDate = format(new Date(ruling.published_at), 'MMM d, yyyy');
  const isWotc = ruling.source === 'wotc';

  return (
    <div className="border-storm border-b pb-3 last:border-b-0 last:pb-0">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-ash text-xs">{formattedDate}</span>
        {isWotc && (
          <span className="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">WotC</span>
        )}
      </div>
      <p className="text-silver text-sm leading-relaxed">{ruling.comment}</p>
    </div>
  );
}

export default function RulingsSection({ cardId }: RulingsSectionProps): ReactElement {
  const { data: rulings, isLoading, error } = useCardRulings(cardId);
  const [showAll, setShowAll] = useState(false);

  const hasRulings = rulings && rulings.length > 0;
  const rulingsCount = rulings?.length ?? 0;
  const shouldCollapse = rulingsCount > INITIAL_RULINGS_COUNT;
  const displayedRulings = showAll || !shouldCollapse ? rulings : rulings?.slice(0, INITIAL_RULINGS_COUNT);

  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-ash flex items-center gap-2 text-xs font-medium tracking-widest uppercase">
            <IoInformationCircle className="h-4 w-4" />
            Rulings
            {hasRulings && (
              <span className="bg-slate text-silver rounded-full px-2 py-0.5 text-[10px] font-medium tracking-normal normal-case">
                {rulingsCount}
              </span>
            )}
          </h3>
        </div>

        {/* Content */}
        {isLoading ? (
          <RulingsContentSkeleton />
        ) : error ? (
          <div className="text-danger py-4 text-center text-sm">Failed to load rulings</div>
        ) : !hasRulings ? (
          <p className="text-ash py-2 text-sm">No rulings for this card.</p>
        ) : (
          <>
            <div className="space-y-3">
              {displayedRulings?.map((ruling, index) => (
                <RulingItem key={`${ruling.published_at}-${index}`} ruling={ruling} />
              ))}
            </div>

            {/* Expand/Collapse Button */}
            {shouldCollapse && (
              <button
                onClick={() => setShowAll(!showAll)}
                className={clsx('text-ash hover:text-silver mt-3 flex items-center gap-1 text-xs transition-colors')}
              >
                {showAll ? (
                  <>
                    <IoChevronUp className="h-3 w-3" />
                    Show fewer
                  </>
                ) : (
                  <>
                    <IoChevronDown className="h-3 w-3" />
                    Show {rulingsCount - INITIAL_RULINGS_COUNT} more rulings
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
